/**
 * Server functions for a representative's "Coach claims" page. They resolve
 * the organization from the URL slug among the organizations the signed-in
 * person represents, so nobody can name another organization's id. Rules
 * live in `~/lib/server/cco-claims`.
 *
 * Aliases: coach claims, confirm claims, not ours, representative claims
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import {
  organizationClaimDecisionInputSchema,
  type OrganizationClaims,
} from "~/lib/shared/cco-claims";
import { UserFacingError } from "~/lib/shared/user-facing-error";

/** The organization behind a slug, when the person represents it. */
async function representedCcoId(
  userId: string,
  slug: string,
): Promise<string | null> {
  const { getMyOrganization } =
    await import("~/lib/server/cco-representatives");
  const organization = await getMyOrganization({ userId, slug });
  return organization?.ccoId ?? null;
}

/** Claims on the organization, or null when the page is not open to them. */
export const getOrganizationClaimsFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ slug: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<OrganizationClaims | null> => {
    const ccoId = await representedCcoId(context.user.id, data.slug);
    if (!ccoId) return null;
    const { listClaimsForOrganization } =
      await import("~/lib/server/cco-claims");
    return listClaimsForOrganization(ccoId, {
      userId: context.user.id,
      clerkUserId: context.user.clerkUserId,
    });
  });

export const decideOrganizationClaimsFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(organizationClaimDecisionInputSchema)
  .handler(async ({ data, context }): Promise<{ decided: number }> => {
    const unavailable = new UserFacingError(
      "Coach claims aren't available for this organization.",
    );
    const ccoId = await representedCcoId(context.user.id, data.slug);
    if (!ccoId) throw unavailable;
    const { decideClaims } = await import("~/lib/server/cco-claims");
    return decideClaims({
      actor: {
        role: "representative",
        ccoId,
        userId: context.user.id,
        clerkUserId: context.user.clerkUserId,
      },
      claims: data.claims,
      decision: data.decision,
      verificationDate: data.verificationDate,
      note: data.note,
    });
  });

export const organizationClaimsQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["organization-claims", slug],
    queryFn: () => getOrganizationClaimsFn({ data: { slug } }),
  });
